import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from 'xlsx';
import { Printer, Search, X, FileDown } from 'lucide-react';
import { printMovement } from '../print/printMovement';
import { downloadAssignmentFormPdf } from '../print/printAssignmentForm';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const PAGE_SIZE = 50;

const TYPE_TO_API = {
  Entrada: 'entrada',
  'Asignación': 'asignacion',
  'Préstamo': 'prestamo',
  'Devolución': 'devolucion',
  Salida: 'salida',
  Consumible: 'consumible',
};

const parseBogotaDateStrict = (raw) => {
  if (raw instanceof Date) return raw;
  if (typeof raw === 'number') return new Date(raw);
  const s = String(raw || '').trim();
  if (!s) return new Date();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
    return new Date(s.replace(' ', 'T') + '-05:00');
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) {
    return new Date(s + '-05:00');
  }
  return new Date(s);
};

const formatBogotaYmdHms = (val) => {
  const d = val instanceof Date ? val : (typeof val === 'number' ? new Date(val) : parseBogotaDateStrict(val));
  const parts = new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

const mapMovement = (mov) => {
  let typed = String(mov?.type || '').toLowerCase();
  if (typed === 'préstamo') typed = 'prestamo';
  if (typed === 'asignación') typed = 'asignacion';
  if (typed === 'devolución') typed = 'devolucion';
  if (typed === 'salida' && String(mov?.notes || '').toLowerCase().includes('consumible')) {
    typed = 'consumible';
  }
  const typeUi = typed === 'entrada'
    ? 'Entrada'
    : typed === 'asignacion'
      ? 'Asignación'
      : typed === 'devolucion'
        ? 'Devolución'
        : typed === 'prestamo'
          ? 'Préstamo'
          : typed === 'consumible'
            ? 'Consumible'
            : 'Salida';
  const unitResolved = mov?.material_unit || 'Unidad';
  let evidencePhotos = [];
  const evRaw = mov?.evidence_photos;
  if (Array.isArray(evRaw)) evidencePhotos = evRaw;
  else if (typeof evRaw === 'string' && evRaw.trim() && evRaw.trim() !== 'null') {
    try {
      const parsed = JSON.parse(evRaw);
      if (Array.isArray(parsed)) evidencePhotos = parsed;
    } catch (_) { /* ignore */ }
  }
  const assigneeType = String(mov?.assignee_type || '').toLowerCase() === 'vehicle' || Number(mov?.vehicle_id || 0) > 0
    ? 'vehicle'
    : 'person';
  return {
    id: mov.id,
    type: typeUi,
    materialId: mov.material_id,
    materialName: mov.material_name || '',
    materialCode: mov.material_code || '',
    materialUnit: unitResolved,
    unit: unitResolved,
    materialCategory: mov.material_category || '',
    responsibleName: mov.requester_name || '',
    responsibleDoc: mov.requester_dni || mov.dni || null,
    quantity: mov.quantity,
    date: mov.created_at ? formatBogotaYmdHms(mov.created_at) : formatBogotaYmdHms(new Date()),
    ts: mov.created_at ? parseBogotaDateStrict(mov.created_at).getTime() : Date.now(),
    user: mov.performed_by_name || mov.performed_by_email || '',
    performed_by_name: mov.performed_by_name || '',
    performed_by_email: mov.performed_by_email || '',
    performed_by_admin_id: typeof mov.performed_by_admin_id !== 'undefined' ? mov.performed_by_admin_id : undefined,
    observations: mov.notes || '',
    entryRef: mov.entry_ref || '',
    status: 'Completado',
    serial: String(mov.serial || '').trim(),
    brandName: String(mov.brand || '').trim(),
    warehouseId: mov.warehouse_id || '',
    ocId: mov.oc_id ? Number(mov.oc_id) : null,
    ocCode: String(mov.oc_code || '').trim(),
    unitCost: (mov.unit_cost !== null && mov.unit_cost !== undefined && Number(mov.unit_cost) > 0)
      ? Number(mov.unit_cost)
      : null,
    ocProvider: String(mov.oc_provider || '').trim(),
    ocProviderNit: String(mov.oc_provider_nit || '').trim(),
    assigneeType,
    vehicleId: Number(mov?.vehicle_id || 0) || null,
    evidencePhotos,
    evidenceCount: evidencePhotos.length,
  };
};

const isAssignmentOrLoanUiType = (typeUi) => {
  const n = String(typeUi || '').toLowerCase().replace('é', 'e').replace('ó', 'o');
  return n === 'asignacion' || n === 'prestamo';
};

export function MovementsModule({ refreshKey = 0 }) {
  const [typeFilter, setTypeFilter] = useState('Todos');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // El histórico solo filtraba por tipo y fecha — para encontrar los
  // movimientos de una persona puntual (ej. "Rober Camayo") había que
  // scrollear a mano por páginas de 50 esperando toparse con su nombre. El
  // backend (movements.php) ya soporta requester_query (LIKE sobre
  // requester_name), solo faltaba exponerlo acá.
  const [responsibleQuery, setResponsibleQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);
  const rowsRef = useRef([]);
  const hasMoreRef = useRef(false);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (offset, { append = false } = {}) => {
    if (append && (inFlightRef.current || !hasMoreRef.current)) return;
    const requestId = ++requestIdRef.current;
    inFlightRef.current = true;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
    setError('');
    try {
      const params = { limit: PAGE_SIZE, offset, order: 'desc' };
      const apiType = typeFilter !== 'Todos' ? TYPE_TO_API[typeFilter] : '';
      if (apiType) params.type = apiType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (responsibleQuery.trim()) params.requester_query = responsibleQuery.trim();
      const res = await api.movements.get(params);
      if (requestId !== requestIdRef.current) return;
      const page = (Array.isArray(res?.data) ? res.data : []).map(mapMovement);
      const more = page.length === PAGE_SIZE;
      hasMoreRef.current = more;
      setHasMore(more);
      setRows((prev) => {
        const next = append ? [...prev, ...page] : page;
        rowsRef.current = next;
        return next;
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error cargando histórico de movimientos:', err);
      setError('No se pudo cargar el histórico. Revisa tu conexión.');
      if (!append) {
        rowsRef.current = [];
        setRows([]);
      }
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [endDate, startDate, typeFilter, responsibleQuery]);

  useEffect(() => {
    const delay = (startDate || endDate || responsibleQuery) ? 250 : 0;
    const t = setTimeout(() => { fetchPage(0, { append: false }); }, delay);
    return () => clearTimeout(t);
  }, [fetchPage, startDate, endDate, responsibleQuery]);

  const seenRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (seenRefreshKeyRef.current === refreshKey) return;
    seenRefreshKeyRef.current = refreshKey;
    fetchPage(0, { append: false });
  }, [fetchPage, refreshKey]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (inFlightRef.current || !hasMoreRef.current) return;
      const offset = rowsRef.current.length;
      if (!offset) return;
      fetchPage(offset, { append: true });
    }, { root, rootMargin: '80px', threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage]);

  const handleSearch = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    fetchPage(0, { append: false });
  };

  const handleClearSearch = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    setStartDate('');
    setEndDate('');
    setResponsibleQuery('');
  };

  const typeBadge = (type) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium border';
    let cls = 'bg-gray-100 text-gray-700 border-gray-200';
    if (type?.startsWith('Entrada')) cls = 'bg-green-100 text-green-700 border-green-200';
    else if (type === 'Asignación') cls = 'bg-blue-100 text-blue-700 border-blue-200';
    else if (type === 'Préstamo') cls = 'bg-indigo-100 text-indigo-700 border-indigo-200';
    else if (type?.toLowerCase().includes('consumible')) cls = 'bg-orange-100 text-orange-700 border-orange-200';
    else if (type?.startsWith('Salida')) cls = 'bg-red-100 text-red-700 border-red-200';
    else if (type === 'Devolución') cls = 'bg-teal-100 text-teal-700 border-teal-200';
    return <span className={`${base} ${cls}`}>{type}</span>;
  };

  const warehouseName = (id) => {
    if (!id) return '-';
    if (id === 'popayan') return 'Popayán';
    if (id === 'santander') return 'Santander de Quilichao';
    return id;
  };

  const formatMoney = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '-';
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);
  };

  const exportToXlsx = () => {
    try {
      const data = (rows || []).map((mv) => ({
        'Fecha': mv.date || '',
        'Tipo': mv.type || '',
        'Material': mv.materialName || mv.materialId || '',
        'Responsable': mv.responsibleName || mv.responsibleDoc || '',
        'Realizado por': mv.user || '',
        'Bodega': warehouseName(mv.warehouseId),
        'Referencia': mv.entryRef || '',
        'OC': mv.ocCode || (mv.ocId ? String(mv.ocId) : ''),
        'Costo unit.': mv.unitCost != null ? mv.unitCost : '',
        'Proveedor OC': mv.ocProvider || '',
        'Cantidad': typeof mv.quantity === 'number' ? mv.quantity : '',
        'Serie': (mv.serial || '').trim() || '',
        'Marca': (mv.brandName || '').trim() || '',
        'Observaciones': mv.observations || '',
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
      const filename = `historico_${new Date().toISOString().slice(0,10)}.xlsx`;
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Histórico de movimientos' });
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Error exportando histórico a .xlsx', err);
      alert('No se pudo exportar el archivo .xlsx.');
    }
  };

  const reprintGroup = (mv) => {
    const rootType = (() => {
      const t = String(mv?.type || '').toLowerCase();
      if (t.startsWith('entrada')) return 'Entrada';
      if (t.startsWith('salida')) return 'Salida';
      if (t.indexOf('asign') >= 0) return 'Asignación';
      if (t.indexOf('devol') >= 0) return 'Devolución';
      return mv?.type || '';
    })();
    const ts0 = typeof mv?.ts === 'number' ? mv.ts : Date.now();
    const wh0 = mv?.warehouseId || '';
    const dni0 = mv?.responsibleDoc || '';
    const group = (rows || []).filter(r => {
      const t = String(r?.type || '').toLowerCase();
      const matchType = rootType === 'Entrada' ? t.startsWith('entrada') : rootType === 'Salida' ? t.startsWith('salida') : rootType === 'Asignación' ? (t === 'asignación' || t === 'asignacion') : rootType === 'Devolución' ? (t === 'devolución' || t === 'devolucion') : t === String(mv?.type || '').toLowerCase();
      const ts = typeof r?.ts === 'number' ? r.ts : 0;
      const close = Math.abs(ts - ts0) <= 60000;
      const sameWh = String(r?.warehouseId || '') === String(wh0 || '');
      const sameDni = String(r?.responsibleDoc || '') === String(dni0 || '');
      return matchType && close && sameWh && (rootType === 'Asignación' ? sameDni : true);
    });
    const first = group[0] || mv;
    const receipt = {
      type: first.type,
      date: first.date,
      materialName: first.materialName,
      materialCode: first.materialCode,
      materialUnit: first.materialUnit,
      materialLocation: first.materialLocation,
      materialStatus: first.materialStatus,
      quantity: group.reduce((acc, r) => acc + (parseInt(r.quantity) || 0), 0),
      observations: first.observations || '',
      serial: first.serial || '',
      brandName: first.brandName || '',
      warehouseId: first.warehouseId,
      warehouseName: warehouseName(first.warehouseId),
      responsibleName: first.responsibleName || '',
      responsibleDoc: first.responsibleDoc || '',
      performed_by_name: first.performed_by_name || first.user || '',
      performed_by_email: first.performed_by_email || '',
      performed_by_admin_id: typeof first.performed_by_admin_id !== 'undefined' ? first.performed_by_admin_id : undefined,
      user: first.user || '',
      items: group.map(r => ({
        materialName: r.materialName || r.materialId,
        materialCode: r.materialCode,
        materialUnit: r.materialUnit,
        unit: r.materialUnit,
        quantity: r.quantity,
        serial: r.serial,
        brandName: r.brandName,
        category: r.category || r.materialCategory || r.material_category
      }))
    };
    printMovement(receipt);
  };

  const downloadAssignmentPdfFromMovement = async (mv) => {
    try {
      let coGroup = '';
      let dotacionCategory = '';
      try {
        const filters = {
          material_id: mv.materialId,
          warehouse_id: mv.warehouseId,
          limit: 5,
          order: 'desc',
        };
        const serial = String(mv.serial || '').trim();
        if (serial) filters.serial = serial;
        const res = await api.assignments.get(filters);
        const list = Array.isArray(res?.data) ? res.data : [];
        const match = list.find((a) => {
          if (serial) return String(a.serial || '').trim() === serial;
          return true;
        }) || list[0];
        if (match) {
          coGroup = String(match.co_group || '').trim();
          dotacionCategory = String(match.dotacion_category || '').trim();
        }
      } catch (_) { /* snapshot opcional */ }

      const assigneeType = mv.assigneeType || 'person';
      const vehiclePlate = assigneeType === 'vehicle' ? (mv.responsibleName || '') : '';
      await downloadAssignmentFormPdf({
        type: mv.type,
        date: mv.date,
        movementId: mv.id,
        entryRef: mv.entryRef || `MV-${mv.id}`,
        assigneeType,
        coGroup,
        dotacionCategory,
        vehiclePlate: assigneeType === 'vehicle' ? vehiclePlate : '',
        responsibleName: mv.responsibleName,
        responsibleDoc: mv.responsibleDoc,
        materialName: mv.materialName,
        materialCode: mv.materialCode,
        materialCategory: mv.materialCategory,
        serial: mv.serial,
        brandName: mv.brandName,
        quantity: mv.quantity,
        observations: mv.observations,
        evidencePhotos: mv.evidencePhotos,
        evidenceCount: mv.evidenceCount,
        warehouseId: mv.warehouseId,
        warehouseName: warehouseName(mv.warehouseId),
        items: [{
          materialName: mv.materialName || mv.materialId,
          materialCode: mv.materialCode,
          quantity: mv.quantity,
          responsibleName: mv.responsibleName,
          responsibleDoc: mv.responsibleDoc,
          serial: mv.serial,
          brandName: mv.brandName,
          evidenceCount: mv.evidenceCount,
        }],
      });
    } catch (e) {
      console.error(e);
      alert('No se pudo generar el PDF del formulario.');
    }
  };

  const statusText = (loading || loadingMore)
    ? 'Cargando…'
    : error
      ? error
      : `Mostrando ${rows.length}${hasMore ? '+' : ''}`;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between w-full mb-4">
        <h3 className="section-title">Historial de Movimientos</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Tipo:</label>
            <select
              className="border rounded px-3 py-1 text-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option>Todos</option>
              <option>Entrada</option>
              <option>Asignación</option>
              <option>Préstamo</option>
              <option>Devolución</option>
              <option>Salida</option>
              <option>Consumible</option>
            </select>
          </div>

          <div className="flex items-center gap-2 border-l pl-3 border-gray-300">
            <label className="text-sm text-gray-700">Responsable:</label>
            <input
              type="text"
              value={responsibleQuery}
              onChange={(e) => setResponsibleQuery(e.target.value)}
              placeholder="Nombre del responsable..."
              className="border rounded px-2 py-1 text-sm w-48"
            />
          </div>

          <div className="flex items-center gap-2 border-l pl-3 border-gray-300">
            <span className="text-sm text-gray-700 font-medium">Fecha:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <span className="text-gray-500">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              title="Buscar por rango de fecha"
            >
              <Search size={16} />
            </button>
            {(startDate || endDate || responsibleQuery) && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="text-gray-500 hover:text-red-600 p-1.5 transition-colors"
                title="Limpiar filtros"
              >
                <X size={16} />
              </button>
            )}
            <span className="text-xs text-gray-600 ml-1">
              {statusText}
            </span>
          </div>

          <button
            type="button"
            onClick={exportToXlsx}
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded ml-auto"
            style={{ backgroundColor: '#04873b' }}
          >
            Descargar
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="overflow-auto border rounded-lg max-h-[32rem]">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 text-left">Fecha</th>
              <th className="px-4 py-2 text-left">Tipo</th>
              <th className="px-4 py-2 text-left">Material</th>
              <th className="px-4 py-2 text-left">Responsable</th>
              <th className="px-4 py-2 text-left">Realizado por</th>
              <th className="px-4 py-2 text-left">Bodega</th>
              <th className="px-4 py-2 text-left">Referencia</th>
              <th className="px-4 py-2 text-left">OC</th>
              <th className="px-4 py-2 text-right">Costo unit.</th>
              <th className="px-4 py-2 text-left">Proveedor</th>
              <th className="px-4 py-2 text-left">Cantidad</th>
              <th className="px-4 py-2 text-left">Serie</th>
              <th className="px-4 py-2 text-left">Marca</th>
              <th className="px-4 py-2 text-left">Observaciones</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-4 py-3 text-gray-500" colSpan={15}>
                  {error || 'No hay movimientos registrados.'}
                </td>
              </tr>
            )}
            {loading && rows.length === 0 && (
              <tr>
                <td className="px-4 py-3 text-gray-500" colSpan={15}>Cargando…</td>
              </tr>
            )}
            {rows.map((mv) => (
              <tr key={mv.id} className="border-t">
                <td className="px-4 py-3">{mv.date}</td>
                <td className="px-4 py-3">{typeBadge(mv.type)}</td>
                <td className="px-4 py-3">{mv.materialName || mv.materialId}</td>
                <td className="px-4 py-3">{mv.responsibleDoc ? `${mv.responsibleName || ''} (${mv.responsibleDoc})` : (mv.responsibleName || '-')}</td>
                <td className="px-4 py-3">{mv.user || '-'}</td>
                <td className="px-4 py-3">{warehouseName(mv.warehouseId)}</td>
                <td className="px-4 py-3">{mv.entryRef || '-'}</td>
                <td className="px-4 py-3">{mv.ocCode || (mv.ocId ? `#${mv.ocId}` : '-')}</td>
                <td className="px-4 py-3 text-right">{mv.unitCost != null ? formatMoney(mv.unitCost) : '-'}</td>
                <td className="px-4 py-3">{mv.ocProvider || '-'}</td>
                <td className="px-4 py-3">{mv.quantity}</td>
                <td className="px-4 py-3">{(mv.serial || '').trim() || '-'}</td>
                <td className="px-4 py-3">{(mv.brandName || '').trim() || '-'}</td>
                <td className="px-4 py-3">{mv.observations}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => reprintGroup(mv)}
                      className="inline-flex items-center gap-2 bg-gray-800 text-white px-3 py-2 rounded"
                    >
                      <Printer size={16} /> Reimprimir
                    </button>
                    {isAssignmentOrLoanUiType(mv.type) && (
                      <button
                        type="button"
                        onClick={() => downloadAssignmentPdfFromMovement(mv)}
                        className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 py-2 rounded"
                      >
                        <FileDown size={16} /> PDF
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {loadingMore && (
              <tr>
                <td className="px-4 py-3 text-gray-500" colSpan={15}>Cargando…</td>
              </tr>
            )}
          </tbody>
        </table>
        <div ref={sentinelRef} className="h-2" aria-hidden="true" />
      </div>
    </div>
  );
}
